import React from 'react';
import Fader from 'react-fader';
import { Route } from 'react-router-dom';
import Switch from 'react-router-transition-switch';

import ApplicationLayout from 'components/ApplicationLayout';
import SettingsSubMenu from 'components/SettingsSubMenu';
import useIsMobile from 'hooks/useIsMobile';
import Booking from 'screens/components/Booking';
import CustomerSupport from 'screens/components/CustomerSupport';
import DashboardReportDetails from 'screens/components/DashboardReports/components/DashboardReportDetails';
import GuestNotAuthorized from 'screens/components/Login/forms/GuestNotAuthorized';
import Onboarding from 'screens/components/Onboarding';
import Root from 'screens/components/Root';
import TravelRequested from 'screens/components/TravelRequested';
import AdminOnboarding from 'screens/containers/AdminOnboarding';
import AirlineException from 'screens/containers/AirlinesException';
import AutosList from 'screens/containers/AutosList';
import BillingCompany from 'screens/containers/BillingCompany';
import BillingCompanyList from 'screens/containers/BillingCompanyList';
import CompanyBillingContainer from 'screens/containers/CompanyBilling';
import CompanyBillingAddressEditContainer from 'screens/containers/CompanyBillingAddressEdit';
import CompanyBillingEditContainer from 'screens/containers/CompanyBillingEdit';
import CompanyBillingMapEditContainer from 'screens/containers/CompanyBillingMapEdit';
import CostCenter from 'screens/containers/CostCenter';
import CostCenterList from 'screens/containers/CostCenterList';
import CostCenterReportContainer from 'screens/containers/CostCenterReportContainer';
import Dashboard from 'screens/containers/Dashboard';
import DashboardReportsContainer from 'screens/containers/DashboardReports';
import Expense from 'screens/containers/Expense';
import ExpensesList from 'screens/containers/ExpensesList';
import ExpensesListImport from 'screens/containers/ExpensesListImport';
import FlightsList from 'screens/containers/FlightsList';
import Group from 'screens/containers/Group';
import GroupList from 'screens/containers/GroupList';
import HotelsList from 'screens/containers/HotelsList';
import InviteContainer from 'screens/containers/Invite';
import Login from 'screens/containers/Login';
import LoginEmail from 'screens/containers/LoginEmail';
import OAuthDefaultContainer from 'screens/containers/OAuthDefaultContainer';
import PaymentAddMethod from 'screens/containers/PaymentAddMethod';
import PaymentCardSelection from 'screens/containers/PaymentCardSelection';
import PaymentGroupCardSelection from 'screens/containers/PaymentGroupCardSelection';
import PaymentTravelStatus from 'screens/containers/PaymentTravelStatus';
import RegistrationOnboardingContainer from 'screens/containers/RegistrationOnboarding';
import RequestHistory from 'screens/containers/RequestHistory';
import Rule from 'screens/containers/Rule';
import GroupSelectRuleContainer from 'screens/containers/RuleSelection';
import RulesListContainer from 'screens/containers/RulesList';
import TravelerProfile from 'screens/containers/TravelerProfile';
import TravelerProfileEdit from 'screens/containers/TravelerProfileEdit';
import TravelerTripPreferences from 'screens/containers/TravelerTripPreferences';
import TravelReportForm from 'screens/containers/TravelReportFormContainer';
import TravelsWithExpensesList from 'screens/containers/TravelsWithExpensesList';
import UserCountrySelectionContainer from 'screens/containers/UserCountrySelection';
import UsersListContainer from 'screens/containers/Users';
import UsersForm from 'screens/containers/UsersForm';
import Welcome from 'screens/containers/Welcome';

const AdminRouter = () => {
  const isMobile = useIsMobile();

  return (
    <Switch
      render={({ children }) => (
        <ApplicationLayout>
          <Fader viewStyle={isMobile ? { height: '100vh' } : {}}>{children}</Fader>
        </ApplicationLayout>
      )}
    >
      <Route path="/invite" component={InviteContainer} />
      <Route path="/registration" component={RegistrationOnboardingContainer} />
      <Route exact path="/adminonboarding" component={AdminOnboarding} />
      <Route exact path="/login-email" component={LoginEmail} />
      <Route path="/login" component={Login} />
      <Route path="/customerSupport" component={CustomerSupport} />
      <Route exact path="/dashboard" component={Dashboard} />
      <Route exact path="/dashboard/v2" component={Dashboard} />
      <Route exact path="/dashboardReports" component={DashboardReportsContainer} />
      <Route exact path="/dashboardReports/reportDetail" component={DashboardReportDetails} />
      <Route exact path="/rules" component={RulesListContainer} />
      <Route exact path="/rule" component={Rule} />
      <Route exact path="/rule/:ruleId" component={Rule} />
      <Route exact path="/payments/travel/:travelId" component={PaymentTravelStatus} />
      <Route exact path="/paymentMethods" component={PaymentCardSelection} />
      <Route exact path="/paymentMethods/add" component={PaymentAddMethod} />
      <Route exact path="/group/:groupId/payment" component={PaymentGroupCardSelection} />
      <Route exact path="/group/:groupId/payment/add" component={PaymentAddMethod} />
      <Route exact path="/group/:groupId" component={Group} />
      <Route exact path="/group" component={Group} />
      <Route exact path="/groupList" component={GroupList} />
      <Route exact path="/costCenterList" component={CostCenterList} />
      <Route exact path="/costCenter/:costCenterId" component={CostCenter} />
      <Route exact path="/costCenter/" component={CostCenter} />
      <Route exact path="/usersList" component={UsersListContainer} />
      <Route exact path="/usersForm" component={UsersForm} />
      <Route exact path="/usersForm/:userId" component={UsersForm} />
      <Route exact path="/travelReport" component={TravelReportForm} />
      <Route path="/travelRequested" component={TravelRequested} />
      <Route path="/booking" component={Booking} />
      <Route path="/flights" component={FlightsList} />
      <Route path="/hotels" component={HotelsList} />
      <Route path="/autos" component={AutosList} />
      <Route exact path="/requestHistory" component={RequestHistory} />
      <Route exact path="/airlineException" component={AirlineException} />
      <Route exact path="/travelsWithExpenses" component={TravelsWithExpensesList} />
      <Route exact path="/expense" component={Expense} />
      <Route exact path="/expense/:expenseId" component={Expense} />
      <Route exact path="/expenses/:travelId" component={ExpensesList} />
      <Route exact path="/expensesImport/:travelId" component={ExpensesListImport} />
      <Route exact path="/travelerProfile" component={TravelerProfile} />
      <Route exact path="/travelerProfileEdit" component={TravelerProfileEdit} />
      <Route exact path="/travelerProfile/preferences/trip" component={TravelerTripPreferences} />
      <Route exact path="/ruleList" component={GroupSelectRuleContainer} />
      <Route exact path="/onboarding" component={Onboarding} />
      <Route exact path="/welcome" component={Welcome} />
      <Route exact path="/companyBilling" component={CompanyBillingContainer} />
      <Route exact path="/companyBillingEdit" component={CompanyBillingEditContainer} />
      <Route exact path="/companyBillingAddressEdit" component={CompanyBillingAddressEditContainer} />
      <Route exact path="/companyBillingMapEdit" component={CompanyBillingMapEditContainer} />
      <Route exact path="/settings" component={SettingsSubMenu} />
      <Route exact path="/oauth-login" component={OAuthDefaultContainer} />
      <Route exact path="/oauth-registration" component={OAuthDefaultContainer} />
      <Route exact path="/oauth-invite" component={OAuthDefaultContainer} />
      <Route exact path="/costCenterReport" component={CostCenterReportContainer} />
      <Route exact path="/oauth-login" component={OAuthDefaultContainer} />
      <Route exact path="/oauth-registration" component={OAuthDefaultContainer} />
      <Route exact path="/oauth-invite" component={OAuthDefaultContainer} />
      <Route exact path="/login-not-authorized" component={GuestNotAuthorized} />
      <Route exact path="/user-country-selection" component={UserCountrySelectionContainer} />
      <Route exact path="/billingCompanyList" component={BillingCompanyList} />
      <Route exact path="/billingCompany/:billingCompanyId" component={BillingCompany} />
      <Route exact path="/billingCompany/" component={BillingCompany} />
      <Route exact path="/" component={Root} />
    </Switch>
  );
};

export default AdminRouter;
